"use client";
// css
import styles from "./AssociateButtonList.module.scss";
// ts
import {
  useEffect,
  useState,
  useRef,
  RefObject,
  useCallback,
  useMemo,
} from "react";
import { openUrl, throttle } from "@/app/utils";
import { handleRobotChat } from "@/app/utils/bizRequest";
import { createUuid, generateUniqueId } from "@/app/utils/string";
//view
import Image from "next/image";
import { Button, Tooltip, message } from "antd";
import { Spin } from "@douyinfe/semi-ui";
//store
// import { batchAddQuestion, addQuestion } from '@store/features/questionSlice';
// import { useAppDispatch, useAppSelector } from '@store/hooks';
import { useMessageStore } from "@/app/store";
import {
  HeaderType,
  MessageSender,
  MessageType,
  MessageStatus,
  RobotMessageType,
  SocketConnectionStatus,
} from "@/app/store/types";
// request
import { queryAIKeyList, queryTagList } from "@/app/api/chatContent";
import { getQuestionType } from "@/app/api/config";
import { webSocketService } from "@/app/services/WebSocketService";
import { useChatInit } from "@/app/hooks/useChatInit";
//type
import {
  ChatContentType,
  QuestionTagType,
  ComponentTypeEnum,
  RobotAIKeywordItem,
} from "@/app/types/typeConfig/chatQuestion";
// hooks
import { useAuth } from "@/app/lib/AuthProvider";
import useReportRequestQueue from "@/app/hooks/robot/useReportRequestQueue";
import {
  ReportBizTypeEnum,
  ReportEventEnum,
  ReportContentEnum,
  ReportKeywordContentEnum,
  ReportParentContentEnum,
  ReportSceneTypeEnum,
} from "@/app/types/typeConfig/report";
import { useCurrentLocale } from "@/app/lib/LocaleProvider";
import Locale from "@/app/locales";
import { isHandyApp, isMobileDevice } from "@/app/utils/device";
import { useSSESend } from "@/app/hooks/robot/useSSESend";

// 定义一个统一的类型用于展示按钮
interface ButtonListItem {
  id: string;
  tagName: string;
  externalLink?: string;
  showTooltip?: boolean;
}

export default function AssociateButtonList({
  onClickCallback,
  type,
}: {
  onClickCallback?: () => void;
  type: string;
}) {
  const locale = useCurrentLocale();
  const { isAuthenticated, login } = useAuth();
  const { checkCanServiceInfo, checkCanService } = useChatInit();
  const checkCanServiceInfoRef = useRef(checkCanServiceInfo);
  useEffect(() => {
    checkCanServiceInfoRef.current = checkCanServiceInfo;
  }, [checkCanServiceInfo]);
  // const dispatch = useAppDispatch();
  // TODO: tag type获取
  // const currentTagType = useAppSelector((state: RootState) => state.questionReducer.tagType);
  // console.log('currentTagType', currentTagType);
  const [isOpen, setIsOpen] = useState(false);
  const [buttonList, setButtonList] = useState<ButtonListItem[]>([]);
  const [loading, setLoading] = useState(false);
  const [showExpand, setShowExpand] = useState(false);
  const buttonRefList = useRef<RefObject<HTMLButtonElement>[]>([]);
  const buttonListEl = useRef<HTMLDivElement>(null);
  const buttonListWrapperEl = useRef<HTMLDivElement>(null);
  const { addToReportQueue, requireReportProps } = useReportRequestQueue();
  const { setRobotMessage, addOutgoingMessage, isConnected } =
    useMessageStore();
  // 添加 ref 追踪最后一次请求的 locale 和 type，避免重复请求
  const lastRequestedRef = useRef<{ locale: string; type: string } | null>(
    null,
  );
  const { handleSSESend } = useSSESend();
  useEffect(() => {
    async function requireTagList() {
      // 如果已经请求过相同的 locale 和 type，跳过
      if (
        lastRequestedRef.current?.locale === locale &&
        lastRequestedRef.current?.type === type
      ) {
        return;
      }

      lastRequestedRef.current = { locale, type };

      setLoading(true);
      // TODO currentTagType先暂时按照0, 后面要改
      let data: QuestionTagType[] | RobotAIKeywordItem[] = [];
      if (type === "SelfService") {
        // TODO: 处理技术问题标签
        data = await queryAIKeyList({ type: 2, languageCode: locale });
      } else {
        data = await queryTagList({ type: 0 });
      }
      // 将不同类型的数据显示转换为统一格式
      const formattedList: ButtonListItem[] = data
        .map((item) => {
          // 判断是否为QuestionTagType类型
          if ("languages" in item) {
            return {
              id: item.id,
              tagName: item.tagName,
              externalLink: item.externalLink,
            } as ButtonListItem;
          } else {
            // RobotAIKeywordItem类型
            return {
              id: item.id,
              tagName: item.keyword,
            } as ButtonListItem;
          }
        })
        .filter((item) => {
          // 对于QuestionTagType，检查语言设置
          const originalItem = data.find((d) => d.id === item.id);
          if (originalItem && "languages" in originalItem) {
            return (
              originalItem.languages
                .toLowerCase()
                .indexOf(locale.toLowerCase()) > -1
            );
          }
          // RobotAIKeywordItem默认显示
          return true;
        });
      setButtonList(formattedList);
      setLoading(false);
    }
    requireTagList();
  }, [locale]);
  useEffect(() => {
    if (!buttonList.length || buttonRefList.current.length === 0) return;
    const updatedButtonList = buttonRefList.current
      .map((item, index) => {
        const flag = item.current ? item.current.offsetWidth > 200 : false;
        return { ...buttonList[index], showTooltip: flag };
      })
      .filter((item) => !!item.id);
    setButtonList((prevList) => {
      if (JSON.stringify(prevList) !== JSON.stringify(updatedButtonList)) {
        return updatedButtonList as ButtonListItem[];
      }
      return prevList;
    });
    if (
      buttonListEl.current &&
      buttonListWrapperEl.current &&
      !isMobileDevice()
    ) {
      const flag =
        buttonListEl.current.scrollWidth >
        buttonListWrapperEl.current.offsetWidth;
      setShowExpand(flag);
    }
  }, [buttonList, buttonRefList]);
  const handleIconClick = () => {
    setIsOpen((isOpen) => !isOpen);
  };
  const throttleClick = useMemo(
    () =>
      throttle(async (item: ButtonListItem) => {
        if (useMessageStore.getState().isAIStreaming) {
          message.warning(Locale.selectHotAIQuestion.replying);
          return;
        }
        // 查找原始数据项以获取完整信息用于报告
        // 这里简化处理，如果需要原始类型信息，可以考虑保留引用
        addToReportQueue({
          bizType: ReportBizTypeEnum.TAG,
          content: item.tagName,
          contentId: item.id,
          createTime: new Date().getTime(),
          eventType: ReportEventEnum.CLICK,
          contentType: ReportContentEnum.TAG,
          parentContentType: ReportParentContentEnum.TAG,
          scene: ReportSceneTypeEnum.NORMAL_CONSULT,
        });

        if (item.externalLink) {
          if (
            item.externalLink.includes("/im") ||
            item.externalLink.includes("/liveChat/")
          ) {
            if (isAuthenticated) {
              if (
                isConnected === SocketConnectionStatus.DISCONNECTED ||
                isConnected === SocketConnectionStatus.CLOSE
              ) {
                // 前置检查：实时请求接口判断客服是否在服务时间
                const canProceed = await checkCanService();
                if (!canProceed) {
                  setRobotMessage({
                    type: MessageType.Text,
                    headerType: HeaderType.MESSAGE,
                    senderType: MessageSender.ROBOT,
                    messageType: RobotMessageType.ROBOT_NORMAL_REPLY,
                    showFeedFooter: false,
                    content: checkCanServiceInfoRef.current?.content || "",
                    timestamp: Date.now(),
                    senderName: "智能助手",
                    robotData: {},
                  });
                  return;
                }
                // 已登录直接出现问题类型选择气泡
                getQuestionType().then((res) => {
                  // 需要选择
                  webSocketService.sendMessage(
                    JSON.stringify({ questionList: res }),
                    {
                      msgType: MessageType.QuestionList,
                      noNeedSendToWebsocket: true,
                      senderType: MessageSender.SYSTEM,
                      msgClassName: "emptyBackground",
                    },
                  );
                });
              } else {
                // 如果已经在会话中，则不进行问题类型选择,提示
                message.info(Locale.chat.hasIMSession);
                return;
              }
            } else {
              login("im");
            }
            // 关闭 popup
            if (onClickCallback) {
              onClickCallback();
            }
            return;
          }
          openUrl(item.externalLink);
          // 关闭 popup
          if (onClickCallback) {
            onClickCallback();
          }
          return;
        } else {
          // 注意：这里可能需要根据实际情况调整，因为我们现在使用的是ButtonListItem而不是QuestionTagType
          const params: any = {
            id: item.id,
            language: locale,
            idType: 2,
            data: {
              userId: createUuid(),
            },
            behaviorCollect: requireReportProps({
              bizType: ReportBizTypeEnum.TAG,
              content: item.tagName,
              contentId: item.id,
              contentType: ReportKeywordContentEnum.KEYWORD_ASSOCIATION,
            }),
          };
          // 如果是在handy app中，则不发送消息
          if (isHandyApp()) {
            const params = {
              content: item.tagName,
              contentId: item.id,
              bizType: ReportBizTypeEnum.AI_CHAT,
              contentType: ReportKeywordContentEnum.AI_KEYWORD,
            };
            handleSSESend(params);
            // 关闭 popup
            if (onClickCallback) {
              onClickCallback();
            }
            return;
          }
          handleRobotChat(params, {
            customerMessage: item.tagName,
            parentContentInfo: {
              parentContentId: item.id,
              parentContent: item.tagName,
              parentContentType: ReportParentContentEnum.TAG,
            },
          }).then((res) => {
            const chatContent = res as ChatContentType[];
            // Insert returned chat content into message store so templates mount
            chatContent.forEach((contentItem) => {
              if (contentItem.type === ComponentTypeEnum.Customer) {
                addOutgoingMessage({
                  headerType: HeaderType.MESSAGE,
                  clientId: createUuid(),
                  content: `{"text":"${contentItem.message || ""}"}`,
                  senderType: MessageSender.USER,
                  type: MessageType.Text,
                  timestamp: Date.now(),
                  status: MessageStatus.Success,
                });
                // if (isHandyApp()) {
                //   const params = {
                //     content: item.tagName,
                //     contentId: item.id,
                //     bizType: ReportBizTypeEnum.AI_CHAT,
                //     contentType: ReportKeywordContentEnum.AI_KEYWORD,
                //   };
                //   handleSSESend(params);
                //   return;
                // } else {
                //   addOutgoingMessage({
                //     headerType: HeaderType.MESSAGE,
                //     clientId: createUuid(),
                //     content: `{"text":"${contentItem.message || ""}"}`,
                //     senderType: MessageSender.USER,
                //     type: MessageType.Text,
                //     timestamp: Date.now(),
                //     status: MessageStatus.Success,
                //   });
                // }
              } else {
                setRobotMessage({
                  id: contentItem.id || "",
                  type: MessageType.Text,
                  headerType: HeaderType.MESSAGE,
                  senderType: MessageSender.ROBOT,
                  messageType: RobotMessageType.ROBOT_ACTION_CARD,
                  showFeedFooter: contentItem.showTechAIFooter ?? true,
                  content: "",
                  timestamp: Date.now(),
                  senderName: "智能助手",
                  robotData: {
                    ...contentItem,
                  },
                });
              }
            });
            if (onClickCallback) {
              onClickCallback();
            }
          });
        }
      }, 1000),
    [
      locale,
      requireReportProps,
      addToReportQueue,
      isConnected,
      onClickCallback,
      checkCanService,
    ],
  );
  const handleClick = useCallback(
    (item: ButtonListItem) => throttleClick(item),
    [throttleClick],
  );
  const Components = isOpen
    ? "closeOutlined"
    : showExpand && (
        <span className={styles.expandIcon}>
          <Image
            onMouseOver={() => setIsOpen(true)}
            src="/images/expand.svg"
            height={16}
            width={16}
            alt=""
          />
        </span>
      );
  if (buttonList.length === 0) {
    if (isMobileDevice() && loading) {
      return (
        <div className={styles.loadingWrapper}>
          <Spin size="large" className={styles.loadingSpin} />
        </div>
      );
    }
    return null;
  }
  const rootClassName = isMobileDevice()
    ? `${styles.mobileNavFooter || styles.navFooter} ${isOpen ? styles.openWrapper : ""}`
    : `${styles.navFooter} ${isOpen ? styles.openWrapper : ""}`;

  return (
    <div
      className={rootClassName}
      onMouseLeave={() => setIsOpen(false)}
      ref={buttonListWrapperEl}
    >
      {isOpen && (
        <div className={styles.title}>{Locale.chatBox.expendButtonTitle}</div>
      )}
      <span className={styles.icon}>{Components}</span>
      <div
        className={
          isMobileDevice()
            ? styles.mobileCategoryBtnList
            : styles.categoryBtnList
        }
        ref={buttonListEl}
      >
        {buttonList.map((item, index) => {
          const tagName = item?.showTooltip ? item.tagName : "";
          return (
            <Tooltip
              title={tagName}
              color="#fff"
              styles={{
                body: {
                  color: "#000",
                  background: "#fff",
                  wordBreak: "break-word",
                },
              }}
              key={index}
            >
              <Button
                shape="round"
                onClick={() => handleClick(item)}
                className={
                  isMobileDevice()
                    ? styles.mobileCategoryButton
                    : styles.categoryButton
                }
              >
                <span className={styles.buttonMessage}>{item.tagName}</span>
              </Button>
            </Tooltip>
          );
        })}
      </div>
    </div>
  );
}
